/** Preferencias locales del BNT Asistente (sin tocar SAT/XML). */

export const BNT_ASISTENTE_AUTO_OPEN_KEY = "visor:bnt_asistente_auto_open";
export const BNT_ASISTENTE_LAST_ACK_KEY = "visor:bnt_asistente_last_ack";

export const BNT_ASISTENTE_CHANNELS = {
  CHAT_PRIVATE: "chat_private",
  CHAT_GROUP: "chat_group",
  MIS_TAREAS: "mis_tareas",
  MIS_NOTAS: "mis_notas",
  ALERTAS: "alertas",
  CLIENTES: "clientes",
};

const EMPTY_ACK = {
  alertsCount: 0,
  chatCount: 0,
  chatGroupCount: 0,
  chatPrivateCount: 0,
  clientMessagesCount: 0,
  personalNotesDueCount: 0,
  assignedTasksPendingCount: 0,
  total: 0,
};

export function extractBntAsistenteCounts(summary = {}) {
  const chatGroupCount = Number(summary?.teamChatGroupCount ?? 0);
  const chatPrivateCount = Number(summary?.teamChatPrivateCount ?? 0);
  const legacyChat = Number(summary?.teamChatHintCount ?? 0);
  const chatCount = chatGroupCount + chatPrivateCount || legacyChat;
  return {
    alertsCount: Number(summary?.alertsCount ?? 0),
    chatCount,
    chatGroupCount: chatGroupCount || (legacyChat && !chatPrivateCount ? legacyChat : chatGroupCount),
    chatPrivateCount,
    clientMessagesCount: Number(summary?.clientMessagesCount ?? 0),
    personalNotesDueCount: Number(summary?.personalNotesDueCount ?? 0),
    assignedTasksPendingCount: Number(summary?.assignedTasksPendingCount ?? 0),
  };
}

export function normalizeBntAsistenteAck(raw = {}) {
  const chatGroupCount = Number(raw.chatGroupCount ?? 0);
  const chatPrivateCount = Number(raw.chatPrivateCount ?? 0);
  const legacyChat = Number(raw.chatCount ?? 0);
  const counts = {
    alertsCount: Number(raw.alertsCount ?? 0),
    chatGroupCount: chatGroupCount || (legacyChat && !chatPrivateCount ? legacyChat : chatGroupCount),
    chatPrivateCount,
    clientMessagesCount: Number(raw.clientMessagesCount ?? 0),
    personalNotesDueCount: Number(raw.personalNotesDueCount ?? 0),
    assignedTasksPendingCount: Number(raw.assignedTasksPendingCount ?? 0),
  };
  counts.chatCount = counts.chatGroupCount + counts.chatPrivateCount || legacyChat;
  counts.total = Number(
    raw.total
    ?? counts.alertsCount
      + counts.chatCount
      + counts.clientMessagesCount
      + counts.personalNotesDueCount
      + counts.assignedTasksPendingCount,
  );
  return counts;
}

export function readBntAsistenteAutoOpen() {
  if (typeof window === "undefined") return true;
  const raw = window.localStorage.getItem(BNT_ASISTENTE_AUTO_OPEN_KEY);
  if (raw === null) return true;
  return raw === "1" || raw === "true";
}

export function writeBntAsistenteAutoOpen(enabled) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(BNT_ASISTENTE_AUTO_OPEN_KEY, enabled ? "1" : "0");
}

export function readBntAsistenteLastAck() {
  if (typeof window === "undefined") return { ...EMPTY_ACK };
  try {
    const raw = window.localStorage.getItem(BNT_ASISTENTE_LAST_ACK_KEY);
    if (!raw) return { ...EMPTY_ACK };
    return normalizeBntAsistenteAck(JSON.parse(raw));
  } catch {
    return { ...EMPTY_ACK };
  }
}

export function writeBntAsistenteLastAck(summary) {
  if (typeof window === "undefined") return;
  const counts = extractBntAsistenteCounts(summary);
  const payload = {
    ...counts,
    total:
      counts.alertsCount
      + counts.chatCount
      + counts.clientMessagesCount
      + counts.personalNotesDueCount
      + counts.assignedTasksPendingCount,
    at: new Date().toISOString(),
  };
  window.localStorage.setItem(BNT_ASISTENTE_LAST_ACK_KEY, JSON.stringify(payload));
  return payload;
}

export function channelToTab(channel) {
  const map = {
    [BNT_ASISTENTE_CHANNELS.CHAT_PRIVATE]: "interno",
    [BNT_ASISTENTE_CHANNELS.CHAT_GROUP]: "interno",
    [BNT_ASISTENTE_CHANNELS.MIS_TAREAS]: "mis-tareas",
    [BNT_ASISTENTE_CHANNELS.MIS_NOTAS]: "mis-notas",
    [BNT_ASISTENTE_CHANNELS.ALERTAS]: "alertas",
    [BNT_ASISTENTE_CHANNELS.CLIENTES]: "clientes",
  };
  return map[channel] || "interno";
}

export function channelsForTab(tab) {
  const map = {
    interno: [BNT_ASISTENTE_CHANNELS.CHAT_PRIVATE, BNT_ASISTENTE_CHANNELS.CHAT_GROUP],
    "mis-tareas": [BNT_ASISTENTE_CHANNELS.MIS_TAREAS],
    "mis-notas": [BNT_ASISTENTE_CHANNELS.MIS_NOTAS],
    alertas: [BNT_ASISTENTE_CHANNELS.ALERTAS],
    clientes: [BNT_ASISTENTE_CHANNELS.CLIENTES],
  };
  return map[tab] || [];
}

const TAB_PRIORITY = [
  BNT_ASISTENTE_CHANNELS.CHAT_PRIVATE,
  BNT_ASISTENTE_CHANNELS.MIS_TAREAS,
  BNT_ASISTENTE_CHANNELS.ALERTAS,
  BNT_ASISTENTE_CHANNELS.MIS_NOTAS,
  BNT_ASISTENTE_CHANNELS.CHAT_GROUP,
  BNT_ASISTENTE_CHANNELS.CLIENTES,
];

function compareCounts(current, baseline, channel) {
  switch (channel) {
    case BNT_ASISTENTE_CHANNELS.CHAT_PRIVATE:
      return current.chatPrivateCount > baseline.chatPrivateCount;
    case BNT_ASISTENTE_CHANNELS.CHAT_GROUP:
      return current.chatGroupCount > baseline.chatGroupCount;
    case BNT_ASISTENTE_CHANNELS.MIS_TAREAS:
      return current.assignedTasksPendingCount > baseline.assignedTasksPendingCount;
    case BNT_ASISTENTE_CHANNELS.MIS_NOTAS:
      return current.personalNotesDueCount > baseline.personalNotesDueCount;
    case BNT_ASISTENTE_CHANNELS.ALERTAS:
      return current.alertsCount > baseline.alertsCount;
    case BNT_ASISTENTE_CHANNELS.CLIENTES:
      return current.clientMessagesCount > baseline.clientMessagesCount;
    default:
      return false;
  }
}

export function detectBntAsistenteUnreadChannels(summary, prevAck = {}) {
  const current = extractBntAsistenteCounts(summary);
  const baseline = normalizeBntAsistenteAck(prevAck);
  return TAB_PRIORITY.filter((channel) => compareCounts(current, baseline, channel));
}

export function detectBntAsistenteIncreasedChannels(summary, prevSummary = {}) {
  const current = extractBntAsistenteCounts(summary);
  const baseline = extractBntAsistenteCounts(prevSummary);
  return TAB_PRIORITY.filter((channel) => compareCounts(current, baseline, channel));
}

export function computeBntAsistenteBadgeTotal(summary) {
  const counts = extractBntAsistenteCounts(summary);
  const ack = readBntAsistenteLastAck();
  const unread = detectBntAsistenteUnreadChannels(summary, ack);
  if (!unread.length) return 0;
  let total = 0;
  if (unread.includes(BNT_ASISTENTE_CHANNELS.ALERTAS)) total += counts.alertsCount - ack.alertsCount;
  if (unread.includes(BNT_ASISTENTE_CHANNELS.CHAT_PRIVATE)) {
    total += counts.chatPrivateCount - ack.chatPrivateCount;
  }
  if (unread.includes(BNT_ASISTENTE_CHANNELS.CHAT_GROUP)) {
    total += counts.chatGroupCount - ack.chatGroupCount;
  }
  if (unread.includes(BNT_ASISTENTE_CHANNELS.CLIENTES)) {
    total += counts.clientMessagesCount - ack.clientMessagesCount;
  }
  if (unread.includes(BNT_ASISTENTE_CHANNELS.MIS_NOTAS)) {
    total += counts.personalNotesDueCount - ack.personalNotesDueCount;
  }
  if (unread.includes(BNT_ASISTENTE_CHANNELS.MIS_TAREAS)) {
    total += counts.assignedTasksPendingCount - ack.assignedTasksPendingCount;
  }
  return Math.max(0, Math.min(total, 99));
}

export function unreadTabsFromChannels(channels = []) {
  const tabs = {};
  channels.forEach((channel) => {
    tabs[channelToTab(channel)] = true;
  });
  return tabs;
}

export function pickBntAsistenteTabForSummary(summary, prevAck = {}, channels = null) {
  const activeChannels = channels || detectBntAsistenteUnreadChannels(summary, prevAck);
  for (const channel of TAB_PRIORITY) {
    if (activeChannels.includes(channel)) return channelToTab(channel);
  }
  const counts = extractBntAsistenteCounts(summary);
  if (counts.chatPrivateCount > 0 || counts.chatGroupCount > 0) return "interno";
  if (counts.assignedTasksPendingCount > 0) return "mis-tareas";
  if (counts.alertsCount > 0) return "alertas";
  if (counts.personalNotesDueCount > 0) return "mis-notas";
  if (counts.clientMessagesCount > 0) return "clientes";
  return "interno";
}

export function ackBntAsistenteChannels(summary, channels, prevAck = {}) {
  const current = extractBntAsistenteCounts(summary);
  const next = normalizeBntAsistenteAck(prevAck);
  channels.forEach((channel) => {
    switch (channel) {
      case BNT_ASISTENTE_CHANNELS.CHAT_PRIVATE:
        next.chatPrivateCount = current.chatPrivateCount;
        break;
      case BNT_ASISTENTE_CHANNELS.CHAT_GROUP:
        next.chatGroupCount = current.chatGroupCount;
        break;
      case BNT_ASISTENTE_CHANNELS.MIS_TAREAS:
        next.assignedTasksPendingCount = current.assignedTasksPendingCount;
        break;
      case BNT_ASISTENTE_CHANNELS.MIS_NOTAS:
        next.personalNotesDueCount = current.personalNotesDueCount;
        break;
      case BNT_ASISTENTE_CHANNELS.ALERTAS:
        next.alertsCount = current.alertsCount;
        break;
      case BNT_ASISTENTE_CHANNELS.CLIENTES:
        next.clientMessagesCount = current.clientMessagesCount;
        break;
      default:
        break;
    }
  });
  next.chatCount = next.chatGroupCount + next.chatPrivateCount;
  next.total =
    next.alertsCount
    + next.chatCount
    + next.clientMessagesCount
    + next.personalNotesDueCount
    + next.assignedTasksPendingCount;
  if (typeof window !== "undefined") {
    window.localStorage.setItem(
      BNT_ASISTENTE_LAST_ACK_KEY,
      JSON.stringify({ ...next, at: new Date().toISOString() }),
    );
  }
  return next;
}
